import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { MapPin, ChevronDown, UserRound, UtensilsCrossed, Utensils, ShoppingBag } from "lucide-react";
import { fetchSite, fetchBranchMenu, publicApi, readSnapshot, siteKey, menuKey } from "./orderApi";
import { BranchPicker } from "./BranchPicker";
import { ItemSheet } from "./ItemSheet";
import { CartBar, CartSheet } from "./Cart";
import { Checkout } from "./Checkout";
import { Home } from "./Home";
import { HomeBK, BottomNav } from "./HomeBK";
import { HomeSig } from "./HomeSig";
import { FullMenu } from "./FullMenu";
import { BrandWall } from "./BrandWall";
import { useCart } from "./useCart";
import { useSession } from "./useSession";
import { useSiteManifest } from "./useSiteManifest";
import "../public/qr/tableOrder.css";
import "./site.css";
import { DiagStrip } from "./DiagStrip";
import { isIosInAppBrowser } from "./browserContext";

const LS_KEY = (slug) => `sc_online_branch_${slug}`;
const CHOICE_KEY = (slug) => `sc_online_branch_choice_${slug}`; // explicit manual choice (localStorage — survives Safari tab reloads / new tabs)
const MODE_KEY = (slug) => `sc_online_mode_${slug}`;
const CHECKOUT_KEY = (slug) => `sc_online_checkout_open_${slug}`; // checkout sheet re-opens after an iOS page discard/reload
const readLS = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
const writeLS = (k, v) => { try { v == null ? localStorage.removeItem(k) : localStorage.setItem(k, v); } catch { /* private mode / quota */ } };
const noteLocate = (slug, reason) => writeLS(`sc_online_locate_log_${slug}`, JSON.stringify({ reason, at: new Date().toISOString() }));

// Scenario A/B/C: auto-pick nearest configured outlet when location is granted; otherwise open the manual picker.
const locateNearest = (slug) => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve({ state: "unsupported" });
  let done = false;
  const finish = (v) => { if (!done) { done = true; resolve(v); } };
  const timer = setTimeout(() => finish({ state: "timeout" }), 7000);
  navigator.geolocation.getCurrentPosition(async (pos) => {
    clearTimeout(timer);
    try {
      const r = await publicApi.get(`/${slug}/nearby`, { params: { lat: pos.coords.latitude, lng: pos.coords.longitude } });
      const nearest = (r.data.outlets || []).find((o) => o.geo_configured && o.distance_km != null);
      finish(nearest ? { state: "ok", branchId: nearest.id } : { state: "none" });
    } catch { finish({ state: "error" }); }
  }, (err) => { clearTimeout(timer); finish({ state: err?.code === 1 ? "denied" : "unavailable" }); }, { timeout: 6500, maximumAge: 300000 });
});

export default function OrderSite() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { pathname } = useLocation();
  // Last known public snapshots render immediately (stale-while-revalidate); fresh server copies replace them.
  const [site, setSite] = useState(() => readSnapshot(siteKey(slug)));
  const [err, setErr] = useState(null);
  const [branchId, setBranchId] = useState(() => localStorage.getItem(LS_KEY(slug)) || null);
  const [menu, setMenu] = useState(() => (branchId ? readSnapshot(menuKey(slug, branchId)) : null));
  const [menuLoading, setMenuLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [openItem, setOpenItem] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpenState] = useState(() => { try { return sessionStorage.getItem(CHECKOUT_KEY(slug)) === "1"; } catch { return false; } });
  const setCheckoutOpen = (v) => { setCheckoutOpenState(v); try { v ? sessionStorage.setItem(CHECKOUT_KEY(slug), "1") : sessionStorage.removeItem(CHECKOUT_KEY(slug)); } catch { /* ignore */ } };
  const [mode, setMode] = useState(() => readLS(MODE_KEY(slug)) || sessionStorage.getItem(`sc_online_mode_${slug}`) || null);
  const [veg, setVeg] = useState("all");
  const diag = new URLSearchParams(window.location.search).get("diag") === "1";
  const itemsById = useMemo(() => { const m = {}; for (const it of menu?.items || []) m[it.id] = it; return m; }, [menu]);
  const cart = useCart(slug, branchId, itemsById);
  const session = useSession(slug);
  useSiteManifest(slug, site);
  useEffect(() => { document.documentElement.dataset.layout = site?.menu_layout || ""; return () => { delete document.documentElement.dataset.layout; }; }, [site?.menu_layout]);
  useEffect(() => { const c = site?.brand?.primary_color; if (c) document.documentElement.style.setProperty("--brand", c); return () => document.documentElement.style.removeProperty("--brand"); }, [site?.brand?.primary_color]);

  // Refresh status for cached (snapshot) data: null | "updating" | "error" | "offline". Cleared only when the
  // relevant refresh succeeds (a 304 is served by the browser as a successful cached 200). A slow, failed or offline
  // request therefore leaves the saved menu visible for longer than one round trip — the indicator says so.
  const [rs, setRs] = useState({ site: null, menu: null });
  const setRefreshFor = (k, v) => setRs((cur) => (cur[k] === v ? cur : { ...cur, [k]: v }));
  const refresh = [rs.menu, rs.site].find((v) => v === "offline" || v === "error") || [rs.menu, rs.site].find((v) => v === "updating") || null;
  const reqSeqRef = useRef(0);          // menu request sequence — late answers for an older slug/branch never win
  const branchIdRef = useRef(branchId); branchIdRef.current = branchId;
  const slugRef = useRef(slug); slugRef.current = slug;
  const failKind = () => (navigator.onLine === false ? "offline" : "error");

  const loadSite = useCallback(async () => {
    const cached = readSnapshot(siteKey(slug));
    if (cached) setRefreshFor("site", "updating");
    try {
      const s = await fetchSite(slug);
      if (slugRef.current !== slug) return null;   // answer for a previous ordering link
      setSite(s);
      setRefreshFor("site", null);
      return s;
    } catch (e) {
      if (slugRef.current !== slug) return null;
      if (!cached) setErr(e.response?.data?.detail?.message || "This ordering link is not available.");
      else setRefreshFor("site", failKind());
      return null;
    }
  }, [slug]);

  useEffect(() => {
    let alive = true;
    loadSite().then((s) => {
      if (!alive || !s) return;
      document.title = `${s.brand?.name || "Order"} · Online Ordering`;
      const ids = s.branches.map((b) => b.id);
      const explicit = readLS(CHOICE_KEY(slug)) || sessionStorage.getItem(CHOICE_KEY(slug));
      if (branchIdRef.current && !ids.includes(branchIdRef.current)) setBranchId(null);
      if (ids.length === 1) { noteLocate(slug, "skipped_single_outlet"); setBranchId(ids[0]); return; }
      if (explicit && ids.includes(explicit)) { noteLocate(slug, "skipped_remembered_outlet"); setBranchId(explicit); return; }
      // A remembered outlet stays selected: no location prompt and no picker on later visits / Safari reloads.
      // (Otherwise every reload re-ran GPS → nearest outlet could replace the chosen one, or the picker opened over the page.)
      if (branchIdRef.current && ids.includes(branchIdRef.current)) { noteLocate(slug, "skipped_remembered_outlet"); return; }
      if (isIosInAppBrowser()) { noteLocate(slug, "skipped_in_app_browser"); setPickerOpen(true); return; }
      noteLocate(slug, branchIdRef.current ? "remembered_outlet_not_in_site" : "no_remembered_outlet");
      locateNearest(slug).then((res) => {
        if (!alive) return;
        if (res.state === "ok" && ids.includes(res.branchId)) { setBranchId(res.branchId); setPickerOpen(false); }
        else setPickerOpen(true);
      });
    });
    return () => { alive = false; };
  }, [slug, loadSite]);

  const loadMenu = useCallback(async (bid) => {
    // Keep the visible menu while refreshing: show the branch's last snapshot (if any) instead of a blank state.
    const seq = ++reqSeqRef.current;
    const snap = readSnapshot(menuKey(slug, bid));
    setMenu((cur) => (cur && cur.branch?.id === bid ? cur : snap));
    setMenuLoading(!snap);
    if (snap) setRefreshFor("menu", "updating");
    try {
      const fresh = await fetchBranchMenu(slug, bid);
      if (seq !== reqSeqRef.current || branchIdRef.current !== bid) return;   // superseded by a newer selection
      setMenu(fresh);
      setRefreshFor("menu", null);
    } catch (e) {
      if (seq !== reqSeqRef.current || branchIdRef.current !== bid) return;
      if (!snap) setErr(e.response?.data?.detail?.message || "Could not load the menu.");
      else setRefreshFor("menu", failKind());
    } finally {
      if (seq === reqSeqRef.current) setMenuLoading(false);
    }
  }, [slug]);
  const retryRefresh = useCallback(() => { loadSite(); if (branchIdRef.current) loadMenu(branchIdRef.current); }, [loadSite, loadMenu]);
  useEffect(() => {
    const onOnline = () => { if (refresh === "offline" || refresh === "error") retryRefresh(); };
    window.addEventListener("online", onOnline);
    return () => window.removeEventListener("online", onOnline);
  }, [refresh, retryRefresh]);

  useEffect(() => { if (!branchId) return; localStorage.setItem(LS_KEY(slug), branchId); loadMenu(branchId); }, [branchId, slug, loadMenu]);

  const branch = site?.branches.find((b) => b.id === branchId) || null;
  const chooseBranch = (id) => { writeLS(CHOICE_KEY(slug), id); setBranchId(id); };
  const closedNotice = menu && menu.availability && !menu.availability.is_open ? menu.availability : null;
  const canOrder = !!site?.ordering_open && !!menu?.ordering_open && !closedNotice;
  const qtyOf = useMemo(() => { const m = {}; for (const l of cart.lines) m[l.item_id] = (m[l.item_id] || 0) + l.qty; return m; }, [cart.lines]);
  const changeQty = (it, delta, fallback) => {
    const idxs = cart.lines.map((l, i) => (l.item_id === it.id ? i : -1)).filter((i) => i >= 0);
    if (idxs.length !== 1) return fallback ? fallback() : setCartOpen(true);
    cart.setQty(idxs[0], cart.lines[idxs[0]].qty + delta);
  };
  const ctrl = {
    onOpen: setOpenItem,
    onAdd: (it) => cart.add({ item_id: it.id, name: it.name, image_url: it.image_url, variant_name: null, addon_ids: [], qty: 1, unit: it.offer_price ?? it.base_price }),
    onQty: changeQty, onCart: () => setCartOpen(true), canOrder,
  };
  const openBanner = (b) => {
    if (!menu) return;
    if (b.link_type === "item") { const it = menu.items.find((i) => i.id === b.target_id); if (it) return setOpenItem(it); }
    if (b.link_type === "combo") { const c = (menu.combos || []).find((i) => i.id === b.target_id); if (c) return setOpenItem({ ...c, is_combo: true, base_price: c.combo_price, is_available: true }); }
    if (b.link_type === "category") nav(`/order/${slug}/menu/${b.target_id}`);
  };
  const color = site?.brand?.primary_color || "#b91c1c";
  const theme = { "--brand": color, "--qr-red": color, "--qr-red-dark": color };
  const sheetsOpen = !!openItem || cartOpen || checkoutOpen;
  const layout = ["premium", "signature"].includes(site?.menu_layout) ? site.menu_layout : "classic";
  const sig = layout === "signature";
  const bk = !sig && site?.home_layout === "burger_king";
  const isHome = pathname.replace(/\/+$/, "") === `/order/${slug}`;
  const navActive = isHome ? "home" : pathname.includes("/menu") ? "menu" : "";
  const effMode = branch ? ((mode === "dine_in" && branch.accepts_dine_in) || (mode === "takeaway" && branch.accepts_takeaway) ? mode : branch.accepts_dine_in ? "dine_in" : "takeaway") : null;
  const pickMode = (m) => { setMode(m); writeLS(MODE_KEY(slug), m); };

  if (err && !site) return <div className="os-site" style={{ display: "grid", placeItems: "center", padding: 24, textAlign: "center" }} data-testid="order-site-unavailable"><div><UtensilsCrossed size={40} /><h1 style={{ fontSize: 24, marginTop: 12 }}>Not available</h1><p>{err}</p></div></div>;
  if (!site) return <div className="os-site" style={{ display: "grid", placeItems: "center" }} data-testid="order-site-loading" role="status"><p>Loading…</p></div>;

  return (
    <main className="os-site qr-experience" data-testid="order-site" style={{ ...theme, background: "#fff" }}>
      {diag && <DiagStrip slug={slug} branchId={branchId} cartCount={cart.count} session={session} />}
      <div className={`os-shell ${bk ? "has-nav" : ""}`}>
        {!(bk && isHome && branch && menu) && <header className="os-header" data-testid="order-site-header">
          <div className="os-header-row">
            {site.brand.logo_url ? <img src={site.brand.logo_url} alt={site.brand.name} className="os-logo" data-testid="brand-logo" /> : <span className="os-logo-mark" aria-hidden="true"><UtensilsCrossed size={20} /></span>}
            <div className="os-brand">
              <h1 data-testid="brand-name">{site.brand.name}</h1>
              <button type="button" onClick={() => setPickerOpen(true)} className="os-branch-btn" data-testid="branch-switch"><MapPin size={13} /><span>{branch ? branch.name : "Select outlet"}</span><ChevronDown size={13} /></button>
            </div>
            <Link to={`/order/${slug}/account`} className="os-icon-btn" aria-label="My orders" data-testid="my-account-link"><UserRound size={18} /></Link>
          </div>
          {branch && (branch.accepts_dine_in || branch.accepts_takeaway) && (
            <div className="os-modes" data-testid="order-modes" aria-label="Ordering modes available">
              {branch.accepts_dine_in && <button type="button" className={`os-mode ${effMode === "dine_in" ? "active" : ""}`} onClick={() => pickMode("dine_in")} aria-pressed={effMode === "dine_in"} data-testid="mode-dine-in"><Utensils size={14} />Dine-in</button>}
              {branch.accepts_takeaway && <button type="button" className={`os-mode ${effMode === "takeaway" ? "active" : ""}`} onClick={() => pickMode("takeaway")} aria-pressed={effMode === "takeaway"} data-testid="mode-takeaway"><ShoppingBag size={14} />Takeaway</button>}
            </div>
          )}
        </header>}

        {!branch && (
          <div className="os-section" style={{ textAlign: "center", padding: "48px 16px" }} data-testid="no-branch-selected">
            <p className="os-hint">Choose an outlet to see its menu.</p>
            <button type="button" className="os-btn primary" style={{ marginTop: 14, width: "auto" }} onClick={() => setPickerOpen(true)} data-testid="choose-branch-btn">Choose outlet</button>
          </div>
        )}
        {branch && closedNotice && <p className="os-notice" role="status" data-testid="ordering-closed-notice">{closedNotice.message || "Online ordering is currently unavailable at this outlet."}</p>}
        {branch && menuLoading && <p className="os-hint" style={{ padding: 24, textAlign: "center" }} data-testid="menu-loading">Loading menu…</p>}
        {refresh && (
          <div className="os-refresh" role="status" data-testid="menu-refresh-status" data-state={refresh}>
            {refresh === "updating" && <span data-testid="menu-refresh-updating">Updating menu…</span>}
            {refresh === "error" && <><span data-testid="menu-refresh-error">Could not refresh menu — showing saved menu</span><button type="button" className="os-btn text" onClick={retryRefresh} data-testid="menu-refresh-retry">Retry</button></>}
            {refresh === "offline" && <><span data-testid="menu-refresh-offline">Offline — showing saved menu</span><button type="button" className="os-btn text" onClick={retryRefresh} data-testid="menu-refresh-retry">Retry</button></>}
          </div>
        )}
        {branch && menu && (
          <Routes>
            <Route index element={sig
              ? <HomeSig slug={slug} site={site} branch={branch} menu={menu} ctrl={ctrl} qtyOf={qtyOf} onBanner={openBanner} sheetsOpen={sheetsOpen} veg={veg} setVeg={setVeg} />
              : bk
              ? <HomeBK slug={slug} site={site} branch={branch} menu={menu} ctrl={ctrl} qtyOf={qtyOf} onBanner={openBanner} sheetsOpen={sheetsOpen} mode={effMode} onMode={pickMode} onPickBranch={() => setPickerOpen(true)} />
              : <Home slug={slug} site={site} branch={branch} menu={menu} ctrl={ctrl} qtyOf={qtyOf} onBanner={openBanner} sheetsOpen={sheetsOpen} />} />
            <Route path="menu" element={<FullMenu slug={slug} menu={menu} layout={layout} cart={cart} ctrl={ctrl} qtyOf={qtyOf} onCart={() => setCartOpen(true)} veg={veg} setVeg={setVeg} />} />
            <Route path="menu/:catId" element={<FullMenu slug={slug} menu={menu} layout={layout} cart={cart} ctrl={ctrl} qtyOf={qtyOf} onCart={() => setCartOpen(true)} veg={veg} setVeg={setVeg} />} />
            <Route path="wall" element={<BrandWall slug={slug} branch={branch} />} />
            <Route path="*" element={<Home slug={slug} site={site} branch={branch} menu={menu} ctrl={ctrl} qtyOf={qtyOf} onBanner={openBanner} sheetsOpen={sheetsOpen} />} />
          </Routes>
        )}
      </div>

      <BranchPicker slug={slug} branches={site.branches} selectedId={branchId} onSelect={chooseBranch} open={pickerOpen} onClose={() => setPickerOpen(false)} cartCount={cart.count} />
      <ItemSheet item={openItem} onClose={() => setOpenItem(null)} orderingOpen={canOrder} onAdd={cart.add} />
      {bk && <BottomNav slug={slug} active={navActive} />}
      <CartBar count={cart.count} total={cart.confirmed ? cart.priced?.total : cart.estimate?.total} provisional={!cart.confirmed} onOpen={() => setCartOpen(true)} aboveNav={bk} />
      <CartSheet open={cartOpen} onClose={() => setCartOpen(false)} cart={cart} onAddMore={() => { setCartOpen(false); nav(`/order/${slug}/menu`); }} onCheckout={() => { setCartOpen(false); setCheckoutOpen(true); }} />
      {branch && <Checkout open={checkoutOpen} onClose={() => setCheckoutOpen(false)} slug={slug} branch={branch} otpWidget={site.otp_widget} cart={cart} color={color} brand={site.brand} session={session} orderType={effMode} onOrderType={pickMode} />}
    </main>
  );
}
